import { useMemo, useState, useEffect } from 'react';
import cronstrue from 'cronstrue';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  value: string;
  timezone: string;
  onChange: (expr: string) => void;
}

type Mode = 'interval' | 'daily' | 'weekly' | 'custom';

const DAYS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function detectMode(expr: string): Mode {
  // Every N minutes -> */N * * * *
  if (/^\*\/\d+ \* \* \* \*$/.test(expr)) return 'interval';
  // Every N hours -> 0 */N * * *
  if (/^0 \*\/\d+ \* \* \*$/.test(expr)) return 'interval';
  // Daily at HH:MM -> MM HH * * *
  if (/^\d+ \d+ \* \* \*$/.test(expr)) return 'daily';
  // Weekly -> MM HH * * D[,D...]
  if (/^\d+ \d+ \* \* [\d,]+$/.test(expr)) return 'weekly';
  return 'custom';
}

export function CronBuilder({ value, timezone, onChange }: Props) {
  const [mode, setMode] = useState<Mode>(() => detectMode(value));

  // Interval state
  const [intervalUnit, setIntervalUnit] = useState<'minutes' | 'hours'>(() => {
    if (/^\*\/\d+ \* \* \* \*$/.test(value)) return 'minutes';
    if (/^0 \*\/\d+ \* \* \*$/.test(value)) return 'hours';
    return 'minutes';
  });
  const [intervalN, setIntervalN] = useState<number>(() => {
    const m = value.match(/^\*\/(\d+) \* \* \* \*$/);
    if (m) return Number(m[1]);
    const h = value.match(/^0 \*\/(\d+) \* \* \*$/);
    if (h) return Number(h[1]);
    return 5;
  });

  // Daily state
  const [dailyHour, setDailyHour] = useState<number>(() => {
    const m = value.match(/^(\d+) (\d+) \* \* \*$/);
    return m ? Number(m[2]) : 9;
  });
  const [dailyMinute, setDailyMinute] = useState<number>(() => {
    const m = value.match(/^(\d+) (\d+) \* \* \*$/);
    return m ? Number(m[1]) : 0;
  });

  // Weekly state
  const [weeklyHour, setWeeklyHour] = useState<number>(() => {
    const m = value.match(/^(\d+) (\d+) \* \* [\d,]+$/);
    return m ? Number(m[2]) : 9;
  });
  const [weeklyMinute, setWeeklyMinute] = useState<number>(() => {
    const m = value.match(/^(\d+) (\d+) \* \* [\d,]+$/);
    return m ? Number(m[1]) : 0;
  });
  const [weeklyDays, setWeeklyDays] = useState<number[]>(() => {
    const m = value.match(/^\d+ \d+ \* \* ([\d,]+)$/);
    return m ? m[1].split(',').map(Number).sort((a, b) => a - b) : [1];
  });

  // Custom state
  const [customExpr, setCustomExpr] = useState<string>(value);

  // Emit on change
  useEffect(() => {
    if (mode === 'interval') {
      const expr = intervalUnit === 'minutes'
        ? `*/${intervalN} * * * *`
        : `0 */${intervalN} * * *`;
      onChange(expr);
    } else if (mode === 'daily') {
      onChange(`${dailyMinute} ${dailyHour} * * *`);
    } else if (mode === 'weekly') {
      const days = weeklyDays.length > 0 ? weeklyDays.join(',') : '1';
      onChange(`${weeklyMinute} ${weeklyHour} * * ${days}`);
    } else if (mode === 'custom') {
      onChange(customExpr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, intervalUnit, intervalN, dailyHour, dailyMinute, weeklyHour, weeklyMinute, weeklyDays, customExpr]);

  const preview = useMemo(() => {
    try {
      const human = cronstrue.toString(value);
      return { ok: true as const, text: `${human} (${timezone})` };
    } catch (err) {
      return { ok: false as const, text: 'Invalid cron expression' };
    }
  }, [value, timezone]);

  const toggleDay = (d: number) => {
    setWeeklyDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );
  };

  return (
    <div className="space-y-3">
      <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="interval">Interval</TabsTrigger>
          <TabsTrigger value="daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly">Weekly</TabsTrigger>
          <TabsTrigger value="custom">Custom</TabsTrigger>
        </TabsList>

        <TabsContent value="interval" className="mt-3 space-y-2">
          <Label htmlFor="cron-interval-n">Every</Label>
          <div className="flex items-center gap-2">
            <Input
              id="cron-interval-n"
              type="number"
              min={1}
              max={intervalUnit === 'hours' ? 23 : 59}
              value={intervalN}
              onChange={(e) => {
                const max = intervalUnit === 'hours' ? 23 : 59;
                setIntervalN(Math.min(max, Math.max(1, Number(e.target.value) || 1)));
              }}
              className="w-24"
            />
            <select
              aria-label="Interval unit"
              value={intervalUnit}
              onChange={(e) => {
                const unit = e.target.value as 'minutes' | 'hours';
                setIntervalUnit(unit);
                if (unit === 'hours') {
                  setIntervalN((n) => Math.min(n, 23));
                }
              }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
            </select>
          </div>
        </TabsContent>

        <TabsContent value="daily" className="mt-3 space-y-2">
          <Label>At</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={23}
              value={dailyHour}
              onChange={(e) => setDailyHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))}
              className="w-20"
            />
            <span className="text-sm">:</span>
            <Input
              type="number"
              min={0}
              max={59}
              value={dailyMinute}
              onChange={(e) => setDailyMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))}
              className="w-20"
            />
            <span className="text-xs text-muted-foreground">({timezone})</span>
          </div>
        </TabsContent>

        <TabsContent value="weekly" className="mt-3 space-y-2">
          <Label>Days</Label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <label key={d.value} className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Checkbox
                  checked={weeklyDays.includes(d.value)}
                  onCheckedChange={() => toggleDay(d.value)}
                />
                {d.label}
              </label>
            ))}
          </div>
          <Label className="mt-3 block">At</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={23}
              value={weeklyHour}
              onChange={(e) => setWeeklyHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))}
              className="w-20"
            />
            <span className="text-sm">:</span>
            <Input
              type="number"
              min={0}
              max={59}
              value={weeklyMinute}
              onChange={(e) => setWeeklyMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))}
              className="w-20"
            />
            <span className="text-xs text-muted-foreground">({timezone})</span>
          </div>
        </TabsContent>

        <TabsContent value="custom" className="mt-3 space-y-2">
          <Label htmlFor="cron-custom">Cron expression</Label>
          <Input
            id="cron-custom"
            value={customExpr}
            onChange={(e) => setCustomExpr(e.target.value)}
            placeholder="*/5 * * * *"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Format: minute hour day-of-month month day-of-week
          </p>
        </TabsContent>
      </Tabs>

      <div
        className={`rounded-md border px-3 py-2 text-sm ${
          preview.ok
            ? 'bg-muted/40 text-foreground'
            : 'border-destructive/40 bg-destructive/10 text-destructive'
        }`}
      >
        <span className="font-mono text-xs text-muted-foreground mr-2">{value}</span>
        <span>{preview.text}</span>
      </div>
    </div>
  );
}
